import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import type { Template } from '../types';

const BalanceSheetOverview: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to render category with multiple transaction rows (for Cash and Retained Earnings)
  const renderCategoryWithTransactions = (categoryName: string, balance: string) => {
    const rows = [];
    const transactionsPerRow = 3;
    const maxRows = 5;
    
    // Always show all 5 rows for Cash and Retained Earnings (empty)
    for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
      rows.push(
        <tr key={`${categoryName}-${rowIndex}`} className="border-b border-gray-300">
          <td className="py-2 px-2 font-medium">{rowIndex === 0 ? categoryName : ''}</td>
          {Array.from({ length: 3 }).map((_, colIndex) => (
            <td 
              key={colIndex} 
              className="activity-cell py-2 px-1 text-right font-mono w-16"
            >
              <span className="cursor-help">
                {'\u00A0'}
              </span>
            </td>
          ))}
          <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
            {rowIndex === 0 ? balance : ''}
          </td>
        </tr>
      );
    }
    
    return rows;
  };

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const response = await apiService.getCurrentFinancialTemplate();
        
        if (response.success && response.template) {
          setTemplate(response.template);
          setError(null);
        } else {
          setError('No financial template found for your organization');
        }
      } catch (err: any) {
        console.error('Failed to fetch financial template:', err);
        
        // Handle specific error cases
        if (err.message.includes('401')) {
          setError('You are not authenticated. Please log in again.');
        } else if (err.message.includes('400')) {
          setError('Your account is not associated with an organization.');
        } else if (err.message.includes('404')) {
          setError('No financial template found for your organization.');
        } else {
          setError('Failed to load financial template. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
    window.scrollTo(0, 0);
  }, []);

  // Get template name with fallback
  const templateName = template?.name || "Financial Statement Analysis Fundamentals";

  const handleStartPractice = async () => {
    try {
      // const sessionResponse = await apiService.getActiveFinancialSession();
      
      // if (sessionResponse?.session?.id) {
      //   // Navigate to practice with actual session ID
      //   navigate(`/financial/practice`);
      // } else {
      //   // No active session, show message
      //   // toast.error('No active practice session found. Please start from the dashboard.');
      //   navigate(`/financial/practice`); // Temporary: navigate to session 1
      // }
      navigate(`/financial/practice`);
    } catch (error) {
      console.error('Failed to get active session:', error);
      toast.error('Unable to start practice. Please try again from the dashboard.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading balance sheet overview...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-6">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Template</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                ← Back to Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-8">
        {/* Header Card - matching EditTemplate styling */}
        <div className="bg-white rounded-lg shadow mb-8" style={{ height: '65px' }}>
          <div className="flex items-center justify-between h-full px-6">
            {/* Left Side - Breadcrumb */}
            <div className="flex items-center space-x-2">
              {/* Back arrow and Dashboard link */}
              <button
                onClick={() => navigate('/dashboard')}
                className="text-blue-400 hover:text-blue-600 hover:underline font-medium text-sm flex items-center space-x-1 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Career Development Worksheets</span>
              </button>
              
              <span className="text-gray-400 text-sm">/</span>
              
              {/* Financial Training link */}
              <button
                onClick={() => navigate(`/financial/welcome`)}
                className="text-blue-400 hover:text-blue-600 hover:underline font-medium text-sm transition-all duration-200"
              >
                {templateName}
              </button>
              
              <span className="text-gray-400 text-sm">/</span>
              
              {/* Page Title */}
              <h1 className="text-lg font-bold text-gray-900">Financial Statement Overview</h1>
            </div>

            {/* Right Side - Balance Sheet Overview */}
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Page Title */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Understanding the Financial Statement</h1>
          <p className="text-gray-600">Learn the format and content before starting practice</p>
        </div>

        {/* Intro Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <p className="text-gray-700 leading-relaxed mb-4">
            The report below is one way of showing how a business is doing. It contains, using words and dollars, information about what the business has and who owns it. Consequently, the <strong>WHAT WE HAVE</strong> (left) side of the report should always equal the <strong>WHO OWNS IT</strong> (right) side.
          </p>
        </div>

        {/* Balance Sheet Visual */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
          <div className="balance-sheet p-4">
            <div className="balance-sheet-grid grid grid-cols-2 gap-4 h-full">
              {/* WHAT WE HAVE (Left side, spans 2 rows) */}
              <div className="bs-section section-have left-side row-span-2 border-2 border-black flex flex-col">
                <div className="section-header bg-green-200 p-3 text-center font-bold border-b-2 border-black" style={{ backgroundColor: '#C6EFCE' }}>WHAT WE HAVE</div>
                <div className="flex-1 flex flex-col">
                  <table className="category-table w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-300 bg-gray-100">
                        <th className="p-2 text-left">Category</th>
                        <th className="p-1 text-center" colSpan={3}>Activity (Amounts)</th>
                        <th className="p-2 text-center bg-gray-100">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Cash - Multi-row */}
                      {renderCategoryWithTransactions('Cash', '$0')}
                      
                      {/* Other asset categories - Single row */}
                      <tr className="border-b border-gray-300">
                        <td className="py-2 px-2 font-medium">Accounts Receivable</td>
                        {Array.from({ length: 3 }).map((_, colIndex) => (
                          <td key={colIndex} className="activity-cell py-2 px-1 text-right font-mono w-16">
                            {' '}
                          </td>
                        ))}
                        <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                          $0
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <td className="py-2 px-2 font-medium">Inventory</td>
                        {Array.from({ length: 3 }).map((_, colIndex) => (
                          <td key={colIndex} className="activity-cell py-2 px-1 text-right font-mono w-16">
                            {' '}
                          </td>
                        ))}
                        <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                          $0
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <td className="py-2 px-2 font-medium">Equipment</td>
                        {Array.from({ length: 3 }).map((_, colIndex) => (
                          <td key={colIndex} className="activity-cell py-2 px-1 text-right font-mono w-16">
                            {' '}
                          </td>
                        ))}
                        <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                          $0
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <td className="py-2 px-2 font-medium">Furniture</td>
                        {Array.from({ length: 3 }).map((_, colIndex) => (
                          <td key={colIndex} className="activity-cell py-2 px-1 text-right font-mono w-16">
                            {' '}
                          </td>
                        ))}
                        <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                          $0
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <td className="py-2 px-2 font-medium">Building</td>
                        {Array.from({ length: 3 }).map((_, colIndex) => (
                          <td key={colIndex} className="activity-cell py-2 px-1 text-right font-mono w-16">
                            {' '}
                          </td>
                        ))}
                        <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                          $0
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <td className="py-2 px-2 font-medium">Land</td>
                        {Array.from({ length: 3 }).map((_, colIndex) => (
                          <td key={colIndex} className="activity-cell py-2 px-1 text-right font-mono w-16">
                            {' '}
                          </td>
                        ))}
                        <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                          $0
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Total Row - positioned at bottom */}
                  <table className="category-table w-full text-xs mt-auto">
                    <tbody>
                      <tr className="section-total bg-green-200 border-t-2 border-green-600">
                        <td className="p-2 font-bold" colSpan={4}>Total: WHAT WE HAVE</td>
                        <td className="balance-cell p-2 text-right font-mono font-bold text-lg bg-gray-100">$0</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* WHAT SOMEONE ELSE OWNS (Top right) */}
              <div className="bs-section section-someone border-2 border-black">
                <div className="section-header bg-pink-200 p-3 text-center font-bold border-b-2 border-black" style={{ backgroundColor: '#FFC7CE' }}>WHAT SOMEONE ELSE OWNS</div>
                <table className="category-table w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-300 bg-gray-100">
                      <th className="p-2 text-left">Category</th>
                      <th className="p-1 text-center" colSpan={3}>Activity (Amounts)</th>
                      <th className="p-2 text-center bg-gray-100">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-300">
                      <td className="py-2 px-2 font-medium">Accounts Payable</td>
                      {Array.from({ length: 3 }).map((_, colIndex) => (
                        <td 
                          key={colIndex} 
                          className="activity-cell py-2 px-1 text-right font-mono w-16"
                        >
                          <span className="cursor-help">
                            {' '}
                          </span>
                        </td>
                      ))}
                      <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                        $0
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300">
                      <td className="py-2 px-2 font-medium">Accrued Payables</td>
                      {Array.from({ length: 3 }).map((_, colIndex) => (
                        <td 
                          key={colIndex} 
                          className="activity-cell py-2 px-1 text-right font-mono w-16"
                        >
                          <span className="cursor-help">
                            {' '}
                          </span>
                        </td>
                      ))}
                      <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                        $0
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300">
                      <td className="py-2 px-2 font-medium">Long-Term Debt</td>
                      {Array.from({ length: 3 }).map((_, colIndex) => (
                        <td 
                          key={colIndex} 
                          className="activity-cell py-2 px-1 text-right font-mono w-16"
                        >
                          <span className="cursor-help">
                            {' '}
                          </span>
                        </td>
                      ))}
                      <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                        $0
                      </td>
                    </tr>
                    <tr className="section-total bg-pink-200 border-t-2 border-pink-600">
                      <td className="p-2 font-bold" colSpan={4}>Total: WHAT SOMEONE ELSE OWNS</td>
                      <td className="balance-cell p-2 text-right font-mono font-bold text-lg bg-gray-100">$0</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* WHAT WE OWN (Bottom right) */}
              <div className="bs-section section-own border-2 border-black">
                <div className="section-header bg-blue-200 p-3 text-center font-bold border-b-2 border-black" style={{ backgroundColor: '#DAEEF3' }}>WHAT WE OWN</div>
                <table className="category-table w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-300 bg-gray-100">
                      <th className="p-2 text-left">Category</th>
                      <th className="p-1 text-center" colSpan={3}>Activity (Amounts)</th>
                      <th className="p-2 text-center bg-gray-100">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-300">
                      <td className="py-2 px-2 font-medium">Common Stock</td>
                      {Array.from({ length: 3 }).map((_, colIndex) => (
                        <td 
                          key={colIndex} 
                          className="activity-cell py-2 px-1 text-right font-mono w-16"
                        >
                          <span className="cursor-help">
                            {' '}
                          </span>
                        </td>
                      ))}
                      <td className="balance-cell py-2 px-2 text-right font-mono font-bold bg-gray-100">
                        $0
                      </td>
                    </tr>
                    {/* Retained Earnings - Multi-row */}
                    {renderCategoryWithTransactions('Retained Earnings', '$0')}
                    <tr className="section-total bg-blue-200 border-t-2 border-blue-600">
                      <td className="p-2 font-bold" colSpan={4}>Total: WHAT WE OWN</td>
                      <td className="balance-cell p-2 text-right font-mono font-bold text-lg bg-gray-100">$0</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* GRAND TOTALS (Must be horizontally aligned) */}
            <div className="grand-totals bg-yellow-50 border-t-2 border-yellow-600 p-4 mt-4">
              <div className="flex justify-between items-center">
                <div className="left-total text-lg font-bold text-gray-900">Total: WHAT WE HAVE = $0</div>
                <div className="right-total text-lg font-bold text-gray-900">Total: SOMEONE ELSE + WE OWN = $0</div>
              </div>
            </div>
          </div>
        </div>

        {/* The Two Questions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Recording Transactions: The Two Questions You'll Answer</h2>
          <p className="text-gray-700 mb-4 text-center">
            For each business scenario, you'll systematically ask:
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Question 1 */}
            <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
              <h3 className="text-lg font-bold text-green-900 mb-2">❓ Question 1:</h3>
              <p className="text-green-900 font-semibold mb-2">Does it affect WHAT WE HAVE?</p>
              <p className="text-green-800 text-sm">If yes, which categories? How much?</p>
            </div>

            {/* Question 2 */}
            <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50">
              <h3 className="text-lg font-bold text-blue-900 mb-2">❓ Question 2:</h3>
              <p className="text-blue-900 font-semibold mb-2">Does it affect WHO OWNS IT?</p>
              <p className="text-blue-800 text-sm">If yes, which categories? How much?</p>
            </div>
          </div>

          <p className="text-center text-gray-700 mt-4 font-medium">
            This simple method works for EVERY business transaction!
          </p>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-center gap-3 mb-6">
          <button
            onClick={() => navigate(`/financial/welcome`)}
            className="inline-flex items-center px-6 py-3 border-2 border-gray-300 text-base font-semibold rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            ← Back
          </button>
          <button
            onClick={handleStartPractice}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-semibold rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
          >
            Got It! Start Practice →
          </button>
        </div>
      </div>
    </div>
    </Layout>
  );
};

export default BalanceSheetOverview;